import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  CalendarBlankIcon, UsersIcon, PhoneIcon, EnvelopeSimpleIcon, MapPinIcon,
  BedIcon, CheckCircleIcon, MountainsIcon,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const todayISO = () => new Date().toISOString().slice(0, 10);
const tomorrowISO = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

export default function PublicBook() {
  const { pid } = useParams();
  const [info, setInfo] = useState(null);
  const [dates, setDates] = useState({ check_in: todayISO(), check_out: tomorrowISO() });
  const [avail, setAvail] = useState([]);
  const [pickedType, setPickedType] = useState(null);
  const [guest, setGuest] = useState({ name: "", phone: "", email: "", adults: 1, children: 0, notes: "" });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/property/${pid}`).then((r) => setInfo(r.data)).catch(() => setInfo("not-found"));
  }, [pid]);

  useEffect(() => {
    if (!pid || !dates.check_in || !dates.check_out) return;
    axios.get(`${API}/public/property/${pid}/availability`, { params: dates })
      .then((r) => setAvail(r.data)).catch(() => setAvail([]));
  }, [pid, dates.check_in, dates.check_out]);

  const nights = Math.max(1, Math.round((new Date(dates.check_out) - new Date(dates.check_in)) / 86400000));
  const total = pickedType ? pickedType.rate_per_night * nights : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedType) return toast.error("Pick a room type");
    if (!guest.name || !guest.phone) return toast.error("Name and phone are required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/book`, {
        property_id: pid,
        room_type: pickedType.room_type,
        check_in: dates.check_in,
        check_out: dates.check_out,
        adults: Number(guest.adults),
        children: Number(guest.children) || 0,
        guest_name: guest.name,
        guest_phone: guest.phone,
        guest_email: guest.email || null,
        notes: guest.notes || null,
      });
      setConfirmed(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed. Please try again.");
    } finally { setBusy(false); }
  };

  if (info === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (info === "not-found") return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Property not found.</div>;

  const brand = info.brand;
  const property = info.property;

  if (confirmed) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader brand={brand} property={property} />
        <div className="max-w-2xl mx-auto p-6 md:p-10">
          <Card className="p-10 text-center">
            <CheckCircleIcon size={48} className="mx-auto text-[hsl(var(--success))] mb-4" weight="duotone" />
            <h1 className="font-display text-3xl">Booking confirmed</h1>
            <p className="text-muted-foreground mt-2">We've held Room {confirmed.room_number} ({confirmed.room_type}) for you.</p>
            <div className="grid grid-cols-2 gap-4 mt-8 text-left">
              <div><p className="label-overline">Check-in</p><p className="font-mono-plex text-lg mt-1">{confirmed.check_in}</p></div>
              <div><p className="label-overline">Check-out</p><p className="font-mono-plex text-lg mt-1">{confirmed.check_out}</p></div>
            </div>
            <div className="mt-8 pt-6 border-t border-border">
              <p className="label-overline">Total</p>
              <p className="font-display text-4xl mt-1">{inr(confirmed.total_amount)}</p>
              <p className="text-xs text-muted-foreground mt-1">Pay on arrival · Reservation ID {confirmed.reservation_id?.slice(0, 8)}</p>
            </div>
            <p className="text-sm text-muted-foreground mt-8">Please carry a valid ID at check-in. See you soon!</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader brand={brand} property={property} />
      <div className="max-w-5xl mx-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <p className="label-overline">1. Dates</p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <Label>Check-in</Label>
                <Input type="date" min={todayISO()} value={dates.check_in} onChange={(e) => setDates({ ...dates, check_in: e.target.value })} data-testid="public-check-in" />
              </div>
              <div>
                <Label>Check-out</Label>
                <Input type="date" min={dates.check_in} value={dates.check_out} onChange={(e) => setDates({ ...dates, check_out: e.target.value })} data-testid="public-check-out" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">{nights} night{nights === 1 ? "" : "s"}</p>
          </Card>

          <Card className="p-6">
            <p className="label-overline">2. Pick your room</p>
            <div className="space-y-3 mt-3">
              {avail.map((t) => {
                const soldOut = t.available === 0;
                const active = pickedType?.room_type === t.room_type;
                return (
                  <button
                    key={t.room_type}
                    disabled={soldOut}
                    onClick={() => setPickedType(t)}
                    className={`w-full text-left border rounded-lg p-4 transition-all flex items-center justify-between ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"} ${soldOut ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`public-room-${t.room_type}`}
                  >
                    <div className="flex items-center gap-4">
                      <BedIcon size={28} weight="duotone" className="text-primary" />
                      <div>
                        <p className="font-display text-lg">{t.room_type}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2">
                          <UsersIcon size={12} /> Fits {t.max_occupancy}
                          {soldOut ? <span className="text-destructive">· Sold out for these dates</span> : <span>· {t.available} left</span>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono-plex text-lg">{inr(t.rate_per_night)}</p>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">per night</p>
                    </div>
                  </button>
                );
              })}
              {avail.length === 0 && <p className="text-sm text-muted-foreground italic text-center py-6">No availability data yet.</p>}
            </div>
          </Card>

          <Card className="p-6">
            <p className="label-overline">3. Guest details</p>
            <form onSubmit={submit} className="space-y-4 mt-3">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} required data-testid="public-guest-name" /></div>
                <div><Label>Phone *</Label><Input value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} required data-testid="public-guest-phone" /></div>
              </div>
              <div><Label>Email (for confirmation)</Label><Input type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} data-testid="public-guest-email" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Adults</Label><Input type="number" min="1" value={guest.adults} onChange={(e) => setGuest({ ...guest, adults: e.target.value })} /></div>
                <div><Label>Children</Label><Input type="number" min="0" value={guest.children} onChange={(e) => setGuest({ ...guest, children: e.target.value })} /></div>
              </div>
              <div><Label>Special requests</Label><Textarea rows={2} value={guest.notes} onChange={(e) => setGuest({ ...guest, notes: e.target.value })} placeholder="Early check-in, extra pillows, etc." /></div>
              <Button type="submit" size="lg" className="w-full" disabled={busy || !pickedType} data-testid="public-submit-btn">
                {busy ? "Booking…" : pickedType ? `Book for ${inr(total)}` : "Pick a room first"}
              </Button>
            </form>
          </Card>
        </div>

        <div className="lg:sticky lg:top-6 h-fit">
          <Card className="overflow-hidden">
            {property.cover_image ? (
              <img src={property.cover_image} alt={property.name} className="w-full h-56 object-cover" />
            ) : (
              <div className="w-full h-56 bg-muted flex items-center justify-center">
                <MountainsIcon size={40} className="text-muted-foreground" weight="duotone" />
              </div>
            )}
            <div className="p-6">
              <h2 className="font-display text-2xl">{property.name}</h2>
              <p className="text-sm text-muted-foreground mt-1 flex items-start gap-2">
                <MapPinIcon size={14} className="mt-0.5 shrink-0" />
                <span>{property.address}, {property.city}, {property.state}</span>
              </p>
              {(property.phone || property.email) && (
                <div className="mt-4 pt-4 border-t border-border space-y-2 text-xs">
                  {property.phone && <p className="flex items-center gap-2"><PhoneIcon size={12} /> {property.phone}</p>}
                  {property.email && <p className="flex items-center gap-2"><EnvelopeSimpleIcon size={12} /> {property.email}</p>}
                </div>
              )}
              {pickedType && (
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="label-overline">Your selection</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span>{pickedType.room_type}</span>
                    <span className="font-mono-plex">{inr(pickedType.rate_per_night)} × {nights}</span>
                  </div>
                  <div className="flex justify-between mt-4 pt-3 border-t border-border">
                    <span className="font-medium">Total</span>
                    <span className="font-display text-2xl">{inr(total)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">GST as applicable at check-in.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
      <footer className="text-center text-xs text-muted-foreground py-6 border-t border-border mt-8">
        Powered by {brand.group_name}
      </footer>
    </div>
  );
}

function PublicHeader({ brand, property }) {
  return (
    <header className="border-b border-border bg-card">
      <div className="max-w-5xl mx-auto p-4 flex items-center gap-3">
        {brand.logo_url ? (
          <img src={brand.logo_url} alt="" className="h-9 w-9 rounded" />
        ) : (
          <div className="h-9 w-9 rounded bg-primary text-primary-foreground flex items-center justify-center">
            <MountainsIcon size={18} weight="duotone" />
          </div>
        )}
        <div>
          <p className="label-overline">{brand.group_name}</p>
          <p className="font-display text-base">{property.name} · Direct booking</p>
        </div>
      </div>
    </header>
  );
}
